import { Trans, t } from "@lingui/macro";
import styled, { css } from "styled-components";
import { Stack } from "~/guidelines/Flex";
import { body1, heading2, heading4, heading5 } from "~/guidelines/Typography";

const Container = styled(Stack)`
  align-items: center;
  max-width: 1200px;
  margin: 80px auto;
  width: 100%;

  @media (max-width: 900px) {
    margin: 32px 0px;
  }
`;

const PreTitle = styled.h5`
  ${heading5}
  margin-bottom: 0px;
  text-align: center;
`;

const Title = styled.h2`
  ${heading2}
  margin-bottom: ${({ theme }) => theme.spacing(8)};
  text-align: center;
`;

const RowsWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  background: ${({ theme }) => theme.color.background.lightThird};
  border: 3px solid ${({ theme }) => theme.color.text.lightFirst};
  overflow: hidden;
  padding: 40px 0px;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    padding: 32px 16px;
    width: calc(100% - 32px);
  }
`;

const commonRowStyles = css`
  display: flex;
  width: 100%;
  gap: 80px;
  padding: 0 40px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 0;
    padding: 0;
  }
`;

const FirstRow = styled.div`
  ${commonRowStyles}
  margin-bottom: 38.5px;

  @media (max-width: 900px) {
    margin-bottom: 0;
  }
`;

const SeparatorLine = styled.div`
  height: 3px;
  background-color: ${({ theme }) => theme.color.text.lightFirst};
  width: 100%;

  @media (max-width: 900px) {
    display: none;
  }
`;

const SecondRow = styled.div`
  ${commonRowStyles}
  margin-top: 38.5px;

  @media (max-width: 900px) {
    margin-top: 0;
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 900px) {
    align-items: flex-start;
    order: 1;
  }
`;

const Value = styled.div`
  ${heading2}
  color: ${({ theme }) => theme.color.text.lightFirst};
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 900px) {
    margin-bottom: 0;
    order: 2;
    text-align: right;
    ${heading4}
  }
`;

const Label = styled.div`
  ${body1}
  text-align: center;

  @media (max-width: 900px) {
    text-align: left;
  }
`;

const Sub = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.color.text.lightSecond};
  text-align: center;
  margin-top: 2px;

  @media (max-width: 900px) {
    text-align: left;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;

  @media (max-width: 900px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    flex: none;
    width: 100%;
  }
`;

const InFigures = () => {
  const FIGURES = [
    { value: "4", label: t`Cities` },
    { value: "8", label: t`Operational establishments` },
    { value: "100", label: t`Chamber in operation` },
    {
      value: "8,7",
      label: t`Average guest score`,
      sub: "(Booking.com, Google, Airbnb)",
    },
    { value: "50", label: t`Hotels by 2028` },
    { value: "€25M", label: t`Assets under management` },
    { value: "+300", label: t`Room in development` },
    { value: "+40K", label: t`Customers welcomed` },
  ];

  const firstRowFigures = FIGURES.slice(0, 4);
  const secondRowFigures = FIGURES.slice(4);

  return (
    <Container>
      <PreTitle>
        <Trans>IN</Trans>
      </PreTitle>

      <Title>
        <Trans>figure</Trans>
      </Title>

      <RowsWrapper>
        <FirstRow>
          {firstRowFigures.map(({ value, label, sub }, i) => (
            <Cell key={`first-${i}`}>
              <Value>{value}</Value>

              <LabelWrapper>
                <Label>{label}</Label>

                {sub && <Sub>{sub}</Sub>}
              </LabelWrapper>
            </Cell>
          ))}
        </FirstRow>

        <SeparatorLine />

        <SecondRow>
          {secondRowFigures.map(({ value, label, sub }, i) => (
            <Cell key={`second-${i}`}>
              <Value>{value}</Value>

              <LabelWrapper>
                <Label>{label}</Label>

                {sub && <Sub>{sub}</Sub>}
              </LabelWrapper>
            </Cell>
          ))}
        </SecondRow>
      </RowsWrapper>
    </Container>
  );
};

export default InFigures;
